import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Divider, Skeleton } from '@mui/material';

import SearchBar from '@components/searchbar/SearchBar';
import Tooltip from '@components/tooltip/Tooltip';
import useOnClickOutside from '@utils/hooks';
import { DropdownOption, DropdownProps } from 'types/dropdown';

import Button from '../button/Button';
import Icon from '../icon/Icon';

import theme from '../../../themes/colors';

const PORTAL_GAP = 4;

const computePortalDropdownPosition = (
  rect: DOMRectReadOnly,
  optionCount: number,
  hasSearchBar: boolean
) => {
  const estimatedHeight = Math.min(
    220,
    16 + (hasSearchBar ? 56 : 0) + Math.max(optionCount, 1) * 48
  );

  const gap = PORTAL_GAP;
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - rect.bottom - gap;
  const spaceAbove = rect.top - gap;

  const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

  const base = {
    left: rect.left,
    width: rect.width
  };

  return shouldOpenUp
    ? {
        ...base,
        top: 0,
        bottom: viewportHeight - rect.top + gap
      }
    : {
        ...base,
        top: rect.bottom + gap,
        bottom: null
      };
};

const Dropdown = (props: DropdownProps) => {
  const {
    filterLabel,
    dropdownId,
    currentFilter,
    filterOptions,
    onSelectFilter,
    className,
    optionsStyle,
    labelStyle,
    isLoading,
    placeholder,
    loadingStyles,
    optionsCount,
    placeholderStyle,
    dropdownHeaderStyle,
    listId,
    disabled,
    placeholderNode,
    searchBarPlaceholder,
    buttonClassName,
    stopEventPropagation = false,
    isSearchDigitsOnly = false,
    tooltipText = '',
    usePortal = false,
    isDividerNeeded = true,
    selectedOptionClassName = 'bg-blue4',
    hoverOptionClassName = 'hover:bg-gray-100',
    optionItemClassName = '',
    onClear,
    rightIconStyle
  } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    bottom: number | null;
  }>({ top: 0, left: 0, width: 0, bottom: null });
  const [isPositionReady, setIsPositionReady] = useState<boolean>(false);

  const ref = useRef(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = (evt?: React.MouseEvent) => {
    setIsDropdownOpen((isOpen) => !isOpen);
    setSearchTerm('');
    if (usePortal) {
      setIsPositionReady(false);
    }
    if (stopEventPropagation) {
      evt?.stopPropagation();
    }
  };

  const onClickFilter = (option: DropdownOption, evt?: React.MouseEvent) => {
    if (stopEventPropagation || usePortal) {
      evt?.stopPropagation();
    }
    if (option.value !== currentFilter && !option.isDisabled) {
      onSelectFilter(option.value);
      toggleDropdown();
    }
  };

  const selectedFilter: DropdownOption | undefined = filterOptions?.find(
    (option) => option.value === currentFilter
  );

  const filteredOptions: DropdownOption[] = searchTerm
    ? filterOptions.filter((option: DropdownOption) =>
        option.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : filterOptions;

  useOnClickOutside(usePortal ? dropdownRef : ref, (e) => {
    if (
      usePortal &&
      ref.current &&
      typeof (ref.current as HTMLElement).contains === 'function' &&
      (ref.current as HTMLElement).contains(e.target as Node)
    ) {
      return;
    }
    e.stopPropagation();
    setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (usePortal && isDropdownOpen && ref.current) {
      const updatePosition = () => {
        if (!ref.current) return;
        const rect = (ref.current as HTMLElement).getBoundingClientRect();
        setPosition(
          computePortalDropdownPosition(
            rect,
            filteredOptions.length,
            Boolean(searchBarPlaceholder)
          )
        );
        setIsPositionReady(true);
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }

    if (!usePortal && isDropdownOpen) {
      const element = document.getElementById(`${listId}-dropdown-list`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom > viewportHeight) {
          element.style.bottom = '100%';
          element.style.top = 'auto';
        } else {
          element.style.top = '100%';
          element.style.bottom = 'auto';
        }
      }
    }

    return undefined;
  }, [
    isDropdownOpen,
    listId,
    usePortal,
    filteredOptions.length,
    searchBarPlaceholder,
    searchTerm
  ]);

  if (isLoading)
    return (
      <div className={loadingStyles || 'w-full'}>
        <Skeleton
          height={53}
          key={Math.random()}
          animation="wave"
          variant="rounded"
        />
      </div>
    );

  const renderDropdown = () => (
    <>
      {searchBarPlaceholder && (
        <div className="my-3">
          <SearchBar
            onSearch={(searchValue) => {
              if (searchTerm === searchValue) return;
              setSearchTerm(searchValue);
            }}
            placeholder={searchBarPlaceholder}
            value={searchTerm}
            stopEventPropagation={stopEventPropagation}
            isSearchDigitsOnly={isSearchDigitsOnly}
          />
        </div>
      )}
      {filteredOptions.length > 0 ? (
        filteredOptions?.map((option, index) => (
          <div key={option?.id}>
            <div
              id={`${filterLabel || 'dropdown'}_${option.value}`}
              className={`my-1 flex items-center justify-between rounded-lg py-2 pl-3 pr-4 text-sm first:pt-3 last:pb-3 ${optionItemClassName} ${
                option.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${
                option.value === selectedFilter?.value
                  ? `${selectedOptionClassName}`
                  : `${hoverOptionClassName}`
              }`}
              role="presentation"
              onClick={(evt) => onClickFilter(option, evt)}
            >
              {option.element ?? option.label}
              {option.value === selectedFilter?.value &&
                (onClear ? (
                  <div
                    role="presentation"
                    className="ml-2 cursor-pointer rounded p-1 hover:bg-red-50"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      onClear();
                      toggleDropdown();
                    }}
                  >
                    <Icon
                      name="trash-red-2"
                      className="!h-5 !min-h-5 !w-5 !min-w-5"
                    />
                  </div>
                ) : (
                  <Icon
                    name="check-mark"
                    className="ml-2 !h-4 !min-h-4 !w-4 !min-w-4"
                    stroke={theme.primary}
                  />
                ))}
            </div>
            {isDividerNeeded && index + 1 !== filterOptions.length && (
              <Divider className="border-grey40" />
            )}
          </div>
        ))
      ) : (
        <div className="mt-3 flex h-40 items-center justify-center px-6 text-base text-grey">
          No results found
        </div>
      )}
    </>
  );

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <div
        className={`flex items-center gap-2 ${dropdownHeaderStyle || ''}`}
        data-tooltip-id={`${dropdownId}-label-tooltip`}
      >
        {filterLabel && (
          <span className="text-base font-medium text-grey">{filterLabel}</span>
        )}
        <Button
          data-testid={dropdownId}
          variant="tertiary"
          handleClick={toggleDropdown}
          size="medium"
          label={
            (selectedFilter?.element ?? selectedFilter?.label) ||
            (placeholderNode ?? placeholder)
          }
          labelCount={optionsCount}
          rightIconName="down-icon-dark"
          className={`min-w-166 justify-between border ${
            className || buttonClassName
          }`}
          labelStyle={
            placeholder && !selectedFilter?.label
              ? `!text-base text-grey !font-normal ${placeholderStyle || ''}`
              : `!font-normal ${labelStyle || ''}`
          }
          rightIconStyle={`${
            isDropdownOpen ? '-rotate-180' : ''
          } ${rightIconStyle} `}
          disabled={disabled}
        />
      </div>
      {tooltipText && (
        <Tooltip
          id={`${dropdownId}-label-tooltip`}
          type="text"
          tooltipText={tooltipText}
        />
      )}
      {isDropdownOpen && usePortal && isPositionReady
        ? createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[9999] mt-1 max-h-55 w-55 overflow-y-auto rounded-md border border-grey40 bg-theme px-2 shadow-sm"
              style={{
                top: position.bottom != null ? 'auto' : `${position.top}px`,
                bottom:
                  position.bottom != null ? `${position.bottom}px` : 'auto',
                left: `${position.left}px`,
                minWidth: `${position.width}px`
              }}
              id={`${listId}-dropdown-list`}
              role="presentation"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {renderDropdown()}
            </div>,
            document.body
          )
        : isDropdownOpen &&
          !usePortal && (
            <div
              className={`absolute z-20 mt-1 w-55 origin-top-right rounded-md border border-grey40 bg-theme px-2 shadow-sm ${
                optionsStyle || 'right-0'
              }`}
              id={`${listId}-dropdown-list`}
            >
              {renderDropdown()}
            </div>
          )}
    </div>
  );
};

export default Dropdown;
